import { localize } from "client_legacy/ndaw/culture";
import { TimeRange } from "../Events/getEventsForDay";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	addDays,
	convertToDateOnly,
	convertToDateTime
} from "client/DateAndTime/dateUtils";
import {
	calculateDayDifference,
	getStartOfDay,
	parseDtoDate,
	addDays as addDaysDateTime
} from "client/DateAndTime/dateTimeUtils";
import { CalendarAppointment } from "../../CalendarPage";
import { calendarLogic } from "client_legacy/Workspace/LiveInformation/Calendar/calendarLogic";
import { AppointmentResponseStatus, CalendarColor } from "client/Api/Calendar/calendar";
import { ObjectWithClientId } from "client/Misc/clientId";

type DayRangeSpanFlags = {
	doesStartOrEndOnRange: boolean;
	doesStartBeforeRange: boolean;
	doesEndAfterRange: boolean;
	isADayOrLonger: boolean;
};

type EventToRenderPosition = {
	row: number;
	left: number;
	right: number;
	isStartDateVisible: boolean;
	isEndDateVisible: boolean;
};

export type RegularHeaderEventToRender = {
	kind: "regular";
	id: string | null;
	position: EventToRenderPosition;
	start: Date;
	end: Date;
	subject: string;
	isRecurring: boolean;
	hasAttachments: boolean;
	isPrivate: boolean;
	isCancelled: boolean;
	color: CalendarColor | null;
	locations: readonly string[] | null;
	isAllDayEvent: boolean;
	myResponseStatus: AppointmentResponseStatus | null;
	isOrganizer: boolean;
	canEdit: boolean;
	calendarId: string;
};

type PlaceholderHeaderEventToRender = {
	kind: "placeholder";
	start: Date;
	end: Date;
	isAllDayEvent: boolean;
	color: CalendarColor | null;
};

export type HeaderEventToRender = ObjectWithClientId<
	(RegularHeaderEventToRender | PlaceholderHeaderEventToRender) & { position: EventToRenderPosition; }
>;

export function getEventsForDateRange(
	appointments: readonly CalendarAppointment[],
	dateRange: readonly DateOnly[],
	timezone: TimeZone
): readonly HeaderEventToRender[] {
	if (dateRange.length === 0) {
		throw Error("There is no date in range");
	}

	const rangeStart: Date = convertToDateTime(dateRange[0], timezone);
	const rangeEnd: Date = convertToDateTime(
		addDays(
			dateRange[dateRange.length - 1],
			1
		),
		timezone
	);

	const range: TimeRange = {
		start: rangeStart.getTime(),
		end: rangeEnd.getTime()
	};

	const appointmentsForDayRange: readonly CalendarAppointmentWithSpanFlags[] = getAppointmentsForDateRange(
		appointments,
		getTimeRanges(dateRange, timezone),
		timezone
	);

	if (appointmentsForDayRange.length === 0) {
		return [];
	}

	const dayColumns: readonly CalendarHeaderDayColumn[] = getCalendarHeaderDayColumns(
		dateRange,
		timezone
	);

	const eventsWithPosition: readonly HeaderEventWithPosition[] = packHeaderEvents(
		appointmentsForDayRange,
		range,
		dayColumns
	);

	return eventsWithPosition.map(convertToEventToRender);
}

export function getTimeRanges(
	dateRange: readonly DateOnly[],
	timezone: TimeZone
): readonly TimeRange[] {
	if (dateRange.length === 0) {
		throw Error("There is no date in range");
	}

	const rangeStart: Date = convertToDateTime(dateRange[0], timezone);
	const rangeEnd: Date = convertToDateTime(
		addDays(
			dateRange[dateRange.length - 1],
			1
		),
		timezone
	);

	const timeRanges: TimeRange[] = [
		{
			start: rangeStart.getTime(),
			end: rangeEnd.getTime()
		}
	];

	for (let i = 1; i < dateRange.length; i++) {
		const currentDay: DateOnly = dateRange[i];
		const prevDay: DateOnly = dateRange[i - 1];
		const currentExpectedDay: DateOnly = addDays(
			prevDay,
			1
		);

		const hasGap: boolean = !currentDay.isEqualTo(currentExpectedDay);

		if (hasGap) {
			timeRanges[timeRanges.length - 1].end = convertToDateTime(
				addDays(
					prevDay,
					1
				),
				timezone
			).getTime();

			const newRangeStart: Date = convertToDateTime(currentDay, timezone);

			const range: TimeRange = {
				start: newRangeStart.getTime(),
				end: rangeEnd.getTime()
			};

			timeRanges.push(range);
		}
	}

	return timeRanges;
}

function convertToEventToRender(
	eventWithPosition: HeaderEventWithPosition
): HeaderEventToRender {
	if (
		eventWithPosition.event
			.kind === "placeholder"
	) {
		return {
			...eventWithPosition.event,
			position: {
				row: eventWithPosition.row,
				left: eventWithPosition.start,
				right: 100 - eventWithPosition.end,
				isStartDateVisible: eventWithPosition.isStartDateVisible,
				isEndDateVisible: eventWithPosition.isEndDateVisible
			}
		};
	}

	return {
		...eventWithPosition.event,
		subject: eventWithPosition.event
			.subject
				? eventWithPosition.event
					.subject
				: localize("_Busy"),
		position: {
			row: eventWithPosition.row,
			left: eventWithPosition.start,
			right: 100 - eventWithPosition.end,
			isStartDateVisible: eventWithPosition.isStartDateVisible,
			isEndDateVisible: eventWithPosition.isEndDateVisible
		}
	};
}

export type CalendarAppointmentWithSpanFlags = CalendarAppointment & DayRangeSpanFlags;

export function getAppointmentsForDateRange(
	appointments: readonly CalendarAppointment[],
	timeRanges: readonly TimeRange[],
	timezone: TimeZone
): readonly CalendarAppointmentWithSpanFlags[] {

	return appointments
		.map(
			x => {
				const {
					doesStartOrEndOnRange,
					doesStartBeforeRange,
					doesEndAfterRange,
					isADayOrLonger
				} = getAppointmentSpanFlags(
					x,
					timeRanges,
					timezone
				);

				return {
					...x,
					doesStartOrEndOnRange,
					doesStartBeforeRange,
					doesEndAfterRange,
					isADayOrLonger
				};
			}
		)
		.filter(
			x => {
				const {
					doesStartOrEndOnRange,
					doesStartBeforeRange,
					doesEndAfterRange,
					isADayOrLonger
				} = x;

				return isADayOrLonger
					&& (doesStartOrEndOnRange || (doesStartBeforeRange && doesEndAfterRange));
			}
		);
}

export function getAppointmentSpanFlags(
	appointment: CalendarAppointment,
	timeRanges: readonly TimeRange[],
	timezone: TimeZone
): DayRangeSpanFlags {
	const {
		startDateToCompare,
		endDateToCompare
	} = calendarLogic.getAppointmentSpanInfo(appointment, timezone);

	if (timeRanges.length === 0) {
		throw new Error("There's no date ranges");
	}

	const rangeStart = timeRanges[0].start;
	const rangeEnd = timeRanges[timeRanges.length -1].end;
	const startTime: number = startDateToCompare.getTime();
	const endTime: number = endDateToCompare.getTime();

	const doesStartOrEndOnRange: boolean = timeRanges.some(
		rangeInfo => {
			return rangeInfo.start < endTime && rangeInfo.end > startTime
			|| startTime < rangeInfo.end && endTime > rangeInfo.start;
		}
	);

	const doesStartBeforeRange: boolean = startTime < rangeStart;
	const doesEndAfterRange: boolean = endTime > rangeEnd;

	const isADayOrLonger: boolean = appointment.isAllDayEvent
		|| calculateDayDifference(
			appointment.end,
			startDateToCompare,
			timezone,
			true
		) >= 1;

	return {
		doesStartOrEndOnRange,
		doesStartBeforeRange,
		doesEndAfterRange,
		isADayOrLonger
	};
}

export type HeaderEventWithPosition = {
	event: CalendarAppointment;
	row: number;
	start: number;
	end: number;
	isStartDateVisible: boolean;
	isEndDateVisible: boolean;
};

export function packHeaderEvents(
	appointments: readonly CalendarAppointmentWithSpanFlags[],
	dateRange: TimeRange,
	dayColumns: readonly CalendarHeaderDayColumn[]
): readonly HeaderEventWithPosition[] {
	const events: CalendarAppointmentWithSpanFlags[] = sortBy(
		appointments,
		(x) => x.start.getTime()
	);

	const result: HeaderEventWithPosition[] = [];

	const rowPlacementTracker: number[] = [];

	for (const event of events) {
		const eventWithPosition: HeaderEventWithPosition = calculatePosition(
			event,
			rowPlacementTracker,
			dateRange,
			dayColumns
		);

		result.push(eventWithPosition);
	}

	return result;
}

function calculatePosition(
	event: CalendarAppointmentWithSpanFlags,
	rowPlacementTracker: number[],
	dateRange: TimeRange,
	dayColumns: readonly CalendarHeaderDayColumn[]
): HeaderEventWithPosition {
	const {
		doesStartBeforeRange,
		doesEndAfterRange
	} = event;

	const {
		start,
		end,
		isStartDateVisible,
		isEndDateVisible
	} = getStartEnd(
		{
			start: event.start,
			end: event.end,
			doesStartBeforeRange,
			doesEndAfterRange,
			dayColumns,
			dateRange
		}
	);

	for (let i = 0; i < rowPlacementTracker.length; i++) {
		if (start >= rowPlacementTracker[i]) {
			rowPlacementTracker[i] = end;

			return {
				event,
				row: i,
				start,
				end,
				isStartDateVisible,
				isEndDateVisible
			};
		}
	}

	const row: number = rowPlacementTracker.length;
	rowPlacementTracker.push(end);

	return {
		event,
		row,
		start,
		end,
		isStartDateVisible,
		isEndDateVisible
	};
}

type EventStartEnd = {
	start: number;
	end: number;
	isStartDateVisible: boolean;
	isEndDateVisible: boolean;
};

type GetStartEndOptions = {
	start: Date;
	end: Date;
	doesStartBeforeRange: boolean;
	doesEndAfterRange: boolean;
	dateRange: TimeRange;
	dayColumns: readonly CalendarHeaderDayColumn[];
};

function getStartEnd(
	options: GetStartEndOptions
): EventStartEnd {
	const {
		doesStartBeforeRange,
		doesEndAfterRange,
		dayColumns,
		dateRange
	} = options;

	const startTimestamp: number = doesStartBeforeRange
		? dateRange.start
		: options.start.getTime();

	const endTimestamp: number = doesEndAfterRange
		? dateRange.end
		: options.end.getTime();

	let startInRange: number = 0;
	let endInRange: number = 0;

	let isStartDateVisible: boolean = false;
	let isEndDateVisible: boolean = false;

	for (let i = 0; i < dayColumns.length; i++) {
		const dayColumn: CalendarHeaderDayColumn = dayColumns[i];

		const {
			day,
			start: dayStart,
			width
		} = dayColumn;

		if (day.start > endTimestamp) {
			break;
		}

		const doesStartInDay: boolean = startTimestamp >= day.start && startTimestamp < day.end;
		const doesStartAfterDay: boolean = startTimestamp >= day.end;
		// Inclusive end so appointments ending after the day columns are full width rendered
		const doesEndInDay: boolean = endTimestamp > day.start && endTimestamp <= day.end;
		const doesEndAfterDay: boolean = endTimestamp > day.end;

		if (doesStartInDay) {
			startInRange = dayStart;
			if (i > 0 || !doesStartBeforeRange) {
				isStartDateVisible = true;
			}
		} else if (doesStartAfterDay) {
			startInRange = dayStart + width;
		}

		if (doesEndInDay) {
			if (i < (dayColumns.length - 1) || !doesEndAfterRange) {
				isEndDateVisible = true;
			}
			endInRange = dayStart + width;
		} else if (doesEndAfterDay) {
			endInRange = dayStart + width;
		}
	}

	if (doesStartBeforeRange) {
		startInRange = 0;
	}

	return {
		start: roundNumber(startInRange),
		end: roundNumber(endInRange),
		isStartDateVisible,
		isEndDateVisible
	};
}

function roundNumber(value: number): number {
	return Math.round((value + Number.EPSILON) * 100) / 100;
}

export type CalendarHeaderDayColumn = {
	day: TimeRange;
	width: number;
	start: number;
	isToday: boolean;
};

export function getCalendarHeaderDayColumns(
	dateRange: readonly DateOnly[],
	timezone: TimeZone
): readonly CalendarHeaderDayColumn[] {
	const dayColumns: CalendarHeaderDayColumn[] = [];
	const dateRangeCount: number = dateRange.length;

	const today: DateOnly = convertToDateOnly(
		new Date(),
		timezone
	);

	const hasTodayInRange: boolean = dateRange.some(x => x.isEqualTo(today));

	const eachDayLength: number = hasTodayInRange
		? 100 / (dateRangeCount + 1)
		: 100 / dateRangeCount;

	let isPastToday: boolean = false;

	for (let i = 0; i < dateRange.length; i++) {
		const date: DateOnly = dateRange[i];
		const isToday: boolean = date.isEqualTo(today);

		const width: number = isToday
			? eachDayLength * 2
			: eachDayLength;

		const start: number = isPastToday
			? (i * eachDayLength) + eachDayLength
			: i * eachDayLength;

		const startOfDay: number = convertToDateTime(date, timezone)
			.getTime();

		const endOfDay: number = convertToDateTime(
			addDays(
				date,
				1
			),
			timezone
		)
			.getTime();

		dayColumns.push({
			day: {
				start: startOfDay,
				end: endOfDay
			},
			width,
			start,
			isToday
		});

		isPastToday = isPastToday || isToday;
	}

	return dayColumns;
}

export function getMoreEventsCountForADay(
	events: readonly HeaderEventToRender[],
	day: Date,
	timezone: TimeZone
): number {
	const startOfTheDay: Date = getStartOfDay(day, timezone);

	const startOfTheNextDay: Date = addDaysDateTime(
		startOfTheDay,
		1,
		timezone
	);

	const eventsInADay: HeaderEventToRender[] = [];

	for (let i = 0; i < events.length; i++) {
		const event: HeaderEventToRender = events[i];
		const start: Date = parseDtoDate(event.start);
		const end: Date = parseDtoDate(event.end);

		const isInTheDay: boolean = start.getTime() < startOfTheNextDay.getTime()
			&& end.getTime() > startOfTheDay.getTime();

		if (isInTheDay) {
			eventsInADay.push(event);
		}
	}

	const rowCount: number = Math.max(
		...eventsInADay.map(x => x.position.row),
		0
	) + 1;

	const extraRows: number = rowCount > DEFAULT_CALENDAR_HEADER_ROW_COUNT
		? rowCount - DEFAULT_CALENDAR_HEADER_ROW_COUNT
		: 0;

	return extraRows;
}

export const DEFAULT_CALENDAR_HEADER_ROW_COUNT = 2;